import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function BeirutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": "Beirut, Lebanon",
    "description": "Beirut is a city of layers and light, the perfect backdrop for a summit about legacy, resilience, and reinvention.",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "33.8886",
      "longitude": "35.4955"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Beirut",
      "addressCountry": "LB"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Why Beirut | Women In Wealth Summit · Lebanon 2026"
        description="Discover why Beirut, Lebanon is the perfect destination for the Women In Wealth Summit. A city of layers, light, resilience, and refinement. Learn about the venue, travel, accommodation, and what makes Beirut unique."
        keywords="Beirut summit, Lebanon summit, why Beirut, Beirut venue, travel to Beirut, Beirut accommodation, Lebanon business events"
        canonical={`${siteUrl}/beirut`}
        schema={schema}
      />
      <Navigation />

      {/* Hero Section with Parallax */}
      <section className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/d57fb5f423044808bd79cd6b76938d07.jpg"
            alt="Beirut Coastline"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>
        </div>

        <div className="relative z-10 h-full flex items-center justify-center px-8 lg:px-16">
          <div className="text-center max-w-4xl">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <p className="text-xs uppercase tracking-widest text-white">The Destination</p>
            </div>
            <h1 className="text-6xl lg:text-8xl font-serif font-bold text-white mb-6 leading-tight">
              Beirut
            </h1>
            <p className="text-2xl lg:text-3xl text-white/90 font-light mb-4">
              Where Resilience Meets Refinement
            </p>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Mediterranean light, layered history, and a creative class that never disappears
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* The City Section */}
      <section className="py-32 bg-white relative">
        <div className="px-8 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-6">The City</p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-8 leading-tight">
                  A City of Layers & Light
                </h2>
                <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                  <p>
                    Beirut is a city that has weathered storms and emerged with grace, culture, and an unshakeable spirit.
                  </p>
                  <p>
                    It is the perfect backdrop for a summit about legacy, resilience, and reinvention — where East meets West, tradition meets innovation, and history meets the future.
                  </p>
                  <p>
                    From its vibrant art scene to its world-class cuisine, from ancient ruins to modern architecture, Beirut offers a unique blend of experiences that mirror the complexity and richness of the women we gather.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Beautiful%20architectural%20detail%20of%20Beirut%20historic%20building%20with%20Mediterranean%20style%20warm%20stone%20elegant%20arches%20cultural%20heritage%20refined%20aesthetic%20editorial%20photography%20natural%20light&width=800&height=1000&seq=beirut004&orientation=portrait"
                    alt="Beirut Architecture"
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#E8B4B8] rounded-full blur-3xl opacity-20 -z-10"></div>
                <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl opacity-20 -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Venue Section */}
      <section className="py-32 bg-gradient-to-br from-[#F8F6F2] to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl opacity-10"></div>
        <div className="px-8 lg:px-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-6">The Venue</p>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-8">
              A Discreet, World-Class Setting
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-12 max-w-2xl mx-auto">
              A five-star sanctuary designed for privacy, elegance, and meaningful connection. Full details shared only with confirmed participants for security and discretion.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl">
                  <i className="ri-calendar-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">Dates</h3>
                <p className="text-gray-600">March 2026</p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl">
                  <i className="ri-global-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">Languages</h3>
                <p className="text-gray-600">English, French, Arabic</p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl">
                  <i className="ri-shirt-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">Dress Code</h3>
                <p className="text-gray-600">Elegant, comfortable, true to your identity</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel & Accommodation Section */}
      <section className="py-32 bg-white">
        <div className="px-8 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
                Travel & Accommodation
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Seamless arrangements designed for your comfort and security
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Getting There */}
              <div className="group relative bg-gradient-to-br from-[#F8F6F2] to-white p-10 rounded-3xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-500 hover:shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37] rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-plane-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    Getting There
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Beirut-Rafic Hariri International Airport is well connected to major cities worldwide. Private aviation arrangements can be coordinated through our concierge team.
                  </p>
                </div>
              </div>

              {/* Accommodation */}
              <div className="group relative bg-gradient-to-br from-[#F8F6F2] to-white p-10 rounded-3xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-500 hover:shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#E8B4B8] rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-hotel-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    Accommodation
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Curated accommodation packages at five-star properties will be shared with confirmed participants. All venues prioritize privacy, security, and exceptional service.
                  </p>
                </div>
              </div>

              {/* Safety & Security */}
              <div className="group relative bg-gradient-to-br from-[#F8F6F2] to-white p-10 rounded-3xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-500 hover:shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37] rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    Safety & Security
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your safety is our priority. We work with trusted local partners and provide comprehensive support throughout your stay.
                  </p>
                </div>
              </div>

              {/* Concierge Support */}
              <div className="group relative bg-gradient-to-br from-[#F8F6F2] to-white p-10 rounded-3xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-500 hover:shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#E8B4B8] rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-customer-service-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    Concierge Support
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our dedicated concierge team is available to assist with all travel arrangements, special requests, and local recommendations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Immersive Image Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            transform: `translateY(${(scrollY - 2000) * 0.3}px)`,
          }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20Beirut%20evening%20scene%20warm%20ambient%20lighting%20sophisticated%20architecture%20Mediterranean%20atmosphere%20cultural%20richness%20refined%20aesthetic%20cinematic%20photography%20golden%20hour%20beautiful%20cityscape&width=1920&height=1080&seq=beirut005&orientation=landscape"
            alt="Beirut Evening"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>

        <div className="relative z-10 h-full flex items-end justify-center px-8 lg:px-16 pb-20">
          <div className="text-center max-w-3xl">
            <p className="text-2xl lg:text-3xl text-white font-serif italic">
              "A city that holds centuries of stories, ready to welcome yours."
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
